import {
  BellPlusIcon,
  BellRinging01Icon,
  Lightbulb02Icon,
  PlusIcon,
  Scales01Icon,
  ZapFastIcon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  Link,
  SetURLSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { ConversationList } from '@/Pages/Conversation/ConversationList';
import GraphFillIcon from '@/Pages/Graph/icons/GraphFillIcon.svg?react';
import { AlertsCreatePane } from '@/components/Alerts/AlertsPane';
import CompositeScorePane from '@/components/CompositeScorePane/CompositeScorePane';
import { ErrorBanner } from '@/components/ErrorBanner';
import {
  SessionsCustomCodeEvaluatorCrudPane,
  SessionsEvaluatorRuleCrudPane,
} from '@/components/EvaluatorCrudPane/SessionsEvaluatorCrudPane';
import { ThreadEvaluatorCrudPane } from '@/components/EvaluatorCrudPane/ThreadEvaluatorCrudPane';
import { SessionRulesDropdown } from '@/components/EvaluatorPopover/EvaluatorsPopover';
import { SessionEvaluatorsTable } from '@/components/Evaluators/EvaluatorsTable';
import { SessionEvaluatorsTableV2 } from '@/components/Evaluators/SessionEvaluatorsTableV2';
import { DEFAULT_FILTERS } from '@/components/FilterBar/constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { ResourceDetailLayout } from '@/components/ResourceDetailTabLayout/ResourceDetailTabLayout';
import {
  ResourceActionsSlot,
  ResourceTabsSlot,
} from '@/components/ResourceDetailTabLayout/ResourceTabLayoutPortals';
import { RuleEditPane } from '@/components/Rules/RuleEditPane';
import { SessionRulesTable } from '@/components/Rules/RulesTable';
import {
  findComparsion,
  flattenAst,
  stringToAst,
} from '@/components/RunsTable/filter/ast';
import { SearchModel } from '@/components/RunsTable/types';
import { parseRelativeDate } from '@/components/RunsTable/utils/getDateTimeRangeLabel';
import { SessionCrudPaneWithButtonV2 } from '@/components/SessionCrudPane/SessionCrudPaneWithButton';
import useToast from '@/components/Toast/useToast';
import { NON_RETRIABLE_STATUS_CODES } from '@/constants/swrDefaultOptions';
import {
  BadgeProps,
  Button,
  TabLabel as TabLabelV2,
  TabList as TabListV2,
  TabPanel as TabPanelV2,
  TabPanels as TabPanelsV2,
} from '@/design-system';
import { useDefaultDuration } from '@/hooks/useDefaultDuration';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { PaneType, usePaneState } from '@/hooks/usePaneState';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useSegmentEventWrapper } from '@/hooks/useSegmentEventWrapper';
import {
  useGroupRunsInfinite,
  useGroupRunsStats,
  useOrganizationId,
  useRunRules,
  useSession,
} from '@/hooks/useSwr';
import { useAlignmentStore } from '@/stores/useAlignmentStore';
import { ResourceType, SessionSchema } from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { RunsTable } from '../../components/RunsTable';
import { useDebouncedFilter } from '../../components/RunsTable/hooks/useDebouncedFilter';
import { useSearchModel } from '../../components/RunsTable/hooks/useSearchModel';
import { TabGroup } from '../../components/Tabs/Tabs';
import { GridTimeModel, useDataGridState } from '../../hooks/useDataGridState';
import {
  appDashboardsIndexPath,
  appProjectsPath,
  appSessionPath,
  deploymentsAppIndexPath,
} from '../../utils/constants';
import { CreateEvaluatorFromExperimentDataDialog } from '../Dataset/components/CreateEvaluatorFromExperimentDataDialog';
import { InsightsJobConfig } from '../InsightsJobConfig/InsightsJobConfig';
import { InsightsJobsTable } from '../InsightsJobs/InsightsJobsTable';
import { ProjectMonitorSetting } from './ProjectMonitorSetting';
import { ProjectSidebarSlot } from './ProjectSidebar.utils';
import { ProjectSidebarV2 } from './ProjectSidebarV2';
import { ProjectStatsV2 } from './ProjectStatsV2';
import ProjectTTLSetting from './ProjectTTLSetting';
import { ProjectNewActionPopoverTrigger } from './components/ProjectNewActionPopoverTrigger';
import { COLUMN_VISIBILITY, LOCAL_STORAGE_KEYS } from './constants';
import {
  useFilterShortcutStats,
  useRunStatsStaggered,
} from './hooks/useRunStatsStaggered';

export function ProjectRunsTableV2({
  sessionId,
  isTestRun,
  searchModel,
  setSearchModel: _setSearchModel,
  updateSearchParams,
}: {
  sessionId: string;
  isTestRun: boolean;
  searchModel: SearchModel;
  setSearchModel: (model: SearchModel) => void;
  updateSearchParams: (params: URLSearchParams) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Use alignment hook instead of props
  const alignmentMode = useAlignmentStore((state) => state.alignmentMode);
  const runRuleId = useAlignmentStore((state) => state.runRuleId);
  const setIsCreateEvaluatorDialogOpen = useAlignmentStore(
    (state) => state.setIsCreateEvaluatorDialogOpen
  );
  const setIsCreatingEvaluatorFromExperimentData = useAlignmentStore(
    (state) => state.setIsCreatingEvaluatorFromExperimentData
  );
  const setAlignmentMode = useAlignmentStore((state) => state.setAlignmentMode);
  const isCreateEvaluatorDialogOpen = useAlignmentStore(
    (state) => state.isCreateEvaluatorDialogOpen
  );
  const isCreatingEvaluatorFromExperimentData = useAlignmentStore(
    (state) => state.isCreatingEvaluatorFromExperimentData
  );
  useEffect(() => {
    return () => setAlignmentMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch run rule data when in alignment mode
  const { data: alignmentRules } = useRunRules({
    id: runRuleId ? [runRuleId] : undefined,
  });

  const view = useMemo(() => {
    const customFilterViewId = searchParams.get('custom_filter_view_id');
    if (customFilterViewId) return customFilterViewId;

    const operands = flattenAst(stringToAst(searchModel.filter))?.operands;
    // Check for errors filter pattern: and(eq(is_root, true), eq(status, "error"))
    if (
      operands?.find(findComparsion('eq', 'is_root', true)) &&
      operands?.find(findComparsion('eq', 'status', 'error'))
    )
      return 'errors';
    if (operands?.find(findComparsion('eq', 'is_root', true))) return 'trace';
    if (operands?.find(findComparsion('eq', 'run_type', 'llm'))) return 'llm';
    return 'trace'; // Default to trace instead of 'all' since we removed it
  }, [searchModel.filter, searchParams]);

  const showTreeView = useMemo(() => {
    const operands = flattenAst(stringToAst(searchModel.filter))?.operands;
    return operands?.find(findComparsion('eq', 'is_root', true));
  }, [searchModel.filter]);

  const { defaultDurationTimeModel } = useDefaultDuration('runsTable');

  const {
    sortModel,
    columnVisibilityModel,
    paginationModel,
    filterModel,
    setSortModel,
    setColumnVisibilityModel,
    // expose orderedFields via search params/localStorage by piggy-backing on sort model storage
    onColumnVisibilityResetClick,
    setPaginationModel,
    resetPaginationModel,
    setFilterModel,
    timeModel,
    setTimeModel,
    initialState,
  } = useDataGridState({
    defaultTimeModel: isTestRun ? undefined : defaultDurationTimeModel,
    defaultColumnVisibility: isTestRun
      ? COLUMN_VISIBILITY.eval
      : COLUMN_VISIBILITY.default,
    columnVisibilityStateKey: isTestRun
      ? LOCAL_STORAGE_KEYS.COLUMN_VISIBILITY_EVAL
      : LOCAL_STORAGE_KEYS.COLUMN_VISIBILITY_DEFAULT,
    updateSearchParams,
  });

  // Persist prioritized column order for runs table in localStorage scoped to project view
  const prioritizedColumnsKey = isTestRun
    ? LOCAL_STORAGE_KEYS.PRIORITIZED_COLUMNS_EVAL
    : LOCAL_STORAGE_KEYS.PRIORITIZED_COLUMNS_DEFAULT;
  const [prioritizedColumns, setPrioritizedColumns] = useLocalStorageState<
    string[]
  >(prioritizedColumnsKey, []);

  const setSearchModel: typeof _setSearchModel = useCallback(
    (arg) => {
      resetPaginationModel();

      return _setSearchModel(arg);
    },
    [resetPaginationModel, _setSearchModel]
  );

  // Create onViewChange handler function for reference
  const onViewChangeHandler = useCallback(
    (
      newView: string,
      maybeFilter?: string,
      maybeTraceFilter?: string,
      maybeTreeFilter?: string,
      isInitialLoad?: boolean
    ) => {
      const isFilterView = !DEFAULT_FILTERS.includes(newView);
      if (isFilterView) {
        const filterViewId = newView;
        const isTogglingOff = filterViewId === view && !isInitialLoad;
        setSearchModel({
          filter: isTogglingOff ? 'eq(is_root, true)' : maybeFilter,
          traceFilter: isTogglingOff ? '' : maybeTraceFilter,
          treeFilter: isTogglingOff ? '' : maybeTreeFilter,
        });
        setSearchParams((params) => {
          if (isTogglingOff) {
            params.delete('custom_filter_view_id');
          } else {
            params.set('custom_filter_view_id', filterViewId);
          }
          return params;
        });
      } else {
        setSearchParams((params) => {
          params.delete('custom_filter_view_id');
          return params;
        });

        const filterMap = {
          trace: 'eq(is_root, true)',
          llm: 'eq(run_type, "llm")',
          errors: 'and(eq(is_root, true), eq(status, "error"))',
        };
        setSearchModel({
          filter: filterMap[newView as keyof typeof filterMap] || '',
        });
      }
    },
    [view, setSearchModel, setSearchParams]
  );

  // Handle alignment mode: switch to "All Runs" view and apply rule filters
  const [didAlignmentModeSetup, setDidAlignmentModeSetup] = useState(false);
  useEffect(() => {
    if (
      alignmentMode &&
      runRuleId &&
      alignmentRules?.length &&
      !didAlignmentModeSetup
    ) {
      const rule = alignmentRules[0];

      // First switch to 'all' view to ensure we're on All Runs tab
      onViewChangeHandler('all');

      // Then apply rule filters, but preserve the 'all' view by not setting restrictive filters
      // We'll apply the rule filter as-is, but let the view logic determine the tab
      if (rule) {
        const newSearchModel: SearchModel = {};
        if (rule.filter) newSearchModel.filter = rule.filter;
        if (rule.trace_filter) newSearchModel.traceFilter = rule.trace_filter;
        if (rule.tree_filter) newSearchModel.treeFilter = rule.tree_filter;

        setSearchModel(newSearchModel);
        setDidAlignmentModeSetup(true);
      }
    }
  }, [
    alignmentMode,
    runRuleId,
    alignmentRules,
    onViewChangeHandler,
    setSearchModel,
    didAlignmentModeSetup,
  ]);

  const runsSessionFilter = useMemo(
    () => ({ session: [sessionId] }),
    [sessionId]
  );
  const debouncedRunsFilter = useDebouncedFilter({
    timeModel,
    runsFilter: runsSessionFilter,
    searchModel,
  });
  const { datasetShareToken } = useParams();

  const {
    stats,
    tokenStats,
    feedbackStats,
    isStatsLoading,
    isTokenStatsLoading,
    isFeedbackStatsLoading,
  } = useRunStatsStaggered(debouncedRunsFilter, datasetShareToken);

  const { allStats, filteredFacets } = useFilterShortcutStats({
    sessionId,
    runsFilter: debouncedRunsFilter,
    datasetShareToken,
  });

  const handleExperimentalSearchToggle = useCallback(
    (enabled: boolean) => {
      // update search model to include experimental search param when toggle is changed
      setSearchModel({
        ...searchModel,
        useExperimentalSearch: enabled,
      });
    },
    [searchModel, setSearchModel]
  );

  return (
    <>
      <RunsTable
        virtual
        view={view}
        tableDisplay={showTreeView ? 'tree' : 'list'}
        withTypeCol={!showTreeView}
        onViewChange={onViewChangeHandler}
        debouncedRunsFilter={debouncedRunsFilter}
        runsFilter={runsSessionFilter}
        searchModel={searchModel}
        setSearchModel={setSearchModel}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        paginationModel={paginationModel}
        filterModel={filterModel}
        setSortModel={setSortModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        onColumnVisibilityResetClick={onColumnVisibilityResetClick}
        setPaginationModel={setPaginationModel}
        resetPaginationModel={resetPaginationModel}
        setFilterModel={setFilterModel}
        timeModel={timeModel}
        setTimeModel={setTimeModel}
        initialState={initialState}
        onExperimentalSearchToggle={handleExperimentalSearchToggle}
        prioritizedColumns={prioritizedColumns}
        setPrioritizedColumns={setPrioritizedColumns}
        enableColumnConfigPane
      />

      <ProjectSidebarSlot.Fill>
        <ProjectStatsV2
          stats={stats}
          isLoadingStats={isStatsLoading}
          tokenStats={tokenStats}
          isLoadingTokenStats={isTokenStatsLoading}
          feedbackStats={feedbackStats}
          isLoadingFeedbackStats={isFeedbackStatsLoading}
          isTraces={debouncedRunsFilter?.is_root}
          useExperimentalSearch={searchModel.useExperimentalSearch}
          source="trace"
        />
        <div className="overflow-hidden border-t border-t-secondary empty:hidden">
          <ProjectSidebarV2
            stats={filteredFacets}
            searchModel={searchModel}
            setSearchModel={setSearchModel}
            allStats={allStats}
          />
        </div>
      </ProjectSidebarSlot.Fill>
      <CreateEvaluatorFromExperimentDataDialog
        open={isCreateEvaluatorDialogOpen}
        isCreating={isCreatingEvaluatorFromExperimentData}
        sessionId={sessionId}
        searchModel={searchModel}
        setSearchModel={setSearchModel}
        onOpenChange={(open) => {
          setIsCreateEvaluatorDialogOpen(open);
          setIsCreatingEvaluatorFromExperimentData(open);
        }}
      />
    </>
  );
}

function ProjectConversations(props: {
  sessionId: string;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}) {
  // store filter in URL search bar
  const { searchParams, setSearchParams } = props;

  const filter = searchParams.get('threadsFilter') || undefined;
  const setFilter = useCallback(
    (filter: string | undefined, setter?: typeof setSearchParams) => {
      const mSetSearchParams = setter ?? setSearchParams;
      mSetSearchParams((params) => {
        params.set('threadsFilter', filter || '');
        return params;
      });
    },
    [setSearchParams]
  );

  const { defaultDurationTimeModel } = useDefaultDuration('conversationsList');
  const [timeModel, setTimeModel] = useState<GridTimeModel>(
    defaultDurationTimeModel
  );

  const timeFilter = useMemo(() => {
    const startDuration = parseRelativeDate(timeModel.duration);
    let startTime = timeModel.start_time || undefined;
    let endTime = timeModel.end_time || undefined;

    if (timeModel.start_time == null && startDuration != null) {
      startTime = dayjs
        .utc()
        .subtract(startDuration.value, startDuration.unit)
        .toISOString();
      endTime = undefined;
    }

    return { start_time: startTime, end_time: endTime };
  }, [timeModel.duration, timeModel.end_time, timeModel.start_time]);

  const { data: stats, isLoading: isStatsLoading } = useGroupRunsStats(
    {
      session_id: props.sessionId,
      group_by: 'conversation',
      start_time: timeFilter.start_time,
      end_time: timeFilter.end_time,
      filter,
    },
    {
      shouldRetryOnError: (error) =>
        !NON_RETRIABLE_STATUS_CODES.includes(error.status),
    }
  );

  const { data: allStats } = useGroupRunsStats(
    {
      session_id: props.sessionId,
      group_by: 'conversation',
      start_time: timeFilter.start_time,
      end_time: timeFilter.end_time,
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: (error) =>
        !NON_RETRIABLE_STATUS_CODES.includes(error.status),
    }
  );

  const pageSize = 20;
  const groups = useGroupRunsInfinite(
    pageSize,
    {
      session_id: props.sessionId,
      group_by: 'conversation',
      start_time: timeFilter.start_time,
      end_time: timeFilter.end_time,
      filter,
    },

    {
      shouldRetryOnError: (error) =>
        !NON_RETRIABLE_STATUS_CODES.includes(error.status),
    }
  );

  const hasMore = useMemo(() => {
    if (!groups.data) return false;
    const lastPage = groups.data[groups.data.length - 1];
    if (!lastPage) return false;
    return lastPage.groups.length === pageSize + 1;
  }, [groups.data]);

  const setSearchModel: (value: SearchModel) => void = useCallback(
    (arg) => {
      return setFilter(arg.filter);
    },
    [setFilter]
  );

  return (
    <>
      <ConversationList
        sessionId={props.sessionId}
        isLoading={groups.isLoading || groups.isValidating}
        groups={groups.data?.flatMap((page) => page.groups) ?? []}
        error={groups.error as unknown as Error | null}
        hasMore={hasMore}
        loadNextPage={() => {
          groups.setSize(groups.size + 1);
        }}
        timeModel={timeModel}
        onTimeModelChange={setTimeModel}
        filter={filter}
        onFilterChange={setFilter}
        stats={stats}
      />
      <ProjectSidebarSlot.Fill>
        <>
          {stats && (
            <ProjectStatsV2
              stats={stats}
              isLoadingStats={isStatsLoading}
              isTraces={true}
              source="conversation"
            />
          )}
          <div className="overflow-hidden border-t border-t-secondary empty:hidden">
            <ProjectSidebarV2
              stats={stats}
              searchModel={{ filter: filter }}
              setSearchModel={setSearchModel}
              allStats={allStats}
            />
          </div>
        </>
      </ProjectSidebarSlot.Fill>
    </>
  );
}

export const StatsSidebarLayout = (props: {
  children: React.ReactNode;
  automate: boolean;
}) => {
  return (
    <PanelGroup
      direction="horizontal"
      autoSaveId={appProjectsPath + appSessionPath}
    >
      <Panel className="flex flex-col !overflow-y-auto overflow-x-hidden">
        {props.children}
      </Panel>
      {!props.automate && (
        <>
          <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-brand-green-400">
            <div className="border-l border-secondary" />

            <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
            <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all group-hover:opacity-100" />
          </PanelResizeHandle>

          <Panel
            defaultSize={20}
            minSize={15}
            className={cn('!overflow-y-auto')}
          >
            <ProjectSidebarSlot.Slot className="h-full" />
          </Panel>
        </>
      )}
    </PanelGroup>
  );
};

export const ProjectPageV2 = ({
  sessionId,
  session,
  onSuccess,
  isPublic = false,
  isLoading,
}: {
  sessionId: string;
  session?: SessionSchema;
  isPublic?: boolean;
  onSuccess?: (id: string, data: SessionSchema) => void;
  isLoading?: boolean;
}) => {
  const { searchModel, setSearchModel, updateSearchParams } = useSearchModel();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTestRun = searchParams.get('test_run') === 'true';

  const setIsCreatingEvaluatorFromExperimentData = useAlignmentStore(
    (state) => state.setIsCreatingEvaluatorFromExperimentData
  );
  const setIsCreateEvaluatorDialogOpen = useAlignmentStore(
    (state) => state.setIsCreateEvaluatorDialogOpen
  );
  const tabIndex = searchParams.get('tab') ?? '0';

  const clioEnabled = useOrgConfig(OrgConfigs.clio_enabled).value;
  const { value: formulasEnabled } = useOrgConfig(
    OrgConfigs.feedback_formulas_enabled
  );

  const selectedClusterJobId = searchParams.get('clusterJobId');
  const selectedClusterId = searchParams.get('clusterId');

  const { createToast } = useToast();
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const { mutate: mutateRunRules } = useRunRules({
    session_id: sessionId,
  });

  const {
    selectedRule,
    setSelectedRule,
    prebuiltEvaluator,
    setPrebuiltEvaluator,
    newPopoverOpen,
    setNewPopoverOpen,
    paneState,
    togglePane: _togglePane,
  } = usePaneState();

  const togglePane = useCallback(
    (pane: PaneType, openState: React.SetStateAction<boolean>) => {
      if (pane === 'thread') {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', '1');
          return newParams;
        });
      }
      _togglePane(pane, openState);
    },
    [_togglePane, setSearchParams]
  );

  const { authorize, isLoading: isPermsLoading } = usePermissions();

  const showTTLUI = useInstanceFlag(InstanceFlags.show_ttl_ui);

  const pageTitleProps = useMemo(
    () => ({
      title: session?.name ?? '',
      id: sessionId,
      type: ResourceType.Project,
      description: session?.description,
      isLoading,
    }),
    [session?.name, sessionId, session?.description, isLoading]
  );

  const getProjectTabs = useMemo(() => {
    const tabs: Array<{
      name: string;
      key: string;
      show: boolean;
      badgeProps?: BadgeProps;
    }> = [];

    // Always show Runs tab
    tabs.push({ name: 'Runs', key: 'runs', show: true });

    // Conditionally show other tabs
    tabs.push({ name: 'Threads', key: 'threads', show: !isTestRun });
    tabs.push({ name: 'Evaluators', key: 'evaluators', show: !isTestRun });
    tabs.push({ name: 'Automations', key: 'automations', show: !isTestRun });
    tabs.push({
      name: 'Insights',
      key: 'insights',
      show: !isTestRun && !!clioEnabled,
      badgeProps: clioEnabled
        ? {
            children: 'Beta',
            color: 'primary',
            rounded: 'sm',
            size: 'sm',
          }
        : undefined,
    });

    return tabs.filter((tab) => tab.show);
  }, [isTestRun, clioEnabled]);

  const track = useSegmentEventWrapper((index: number) => {
    const tabName = getProjectTabs[index].key;
    return [
      'project_tab_change',
      {
        tab_name: tabName,
        location: 'tracing_project_page',
      },
    ];
  });
  // purely for tracking purposes
  const handleRunsTabClick = track(() => void 0);

  if (isPermsLoading) {
    return <LinearProgress />;
  }
  if (!authorize('projects:read')) {
    return (
      <ErrorBanner className="m-4">
        Can't access tracing projects. Contact your administrator to give you
        the project:read access
      </ErrorBanner>
    );
  }

  const handStartAlignEvaluator = () => {
    setIsCreatingEvaluatorFromExperimentData(true);
    setIsCreateEvaluatorDialogOpen(true);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', '0');
      return newParams;
    });
  };

  const handleEvaluatorSave = () => {
    mutateRunRules();
    navigate(
      `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}?tab=2`
    );
    createToast({
      title: 'Evaluator saved successfully',
    });
  };

  return (
    <ProjectSidebarSlot.Context>
      <ResourceDetailLayout pageTitleProps={pageTitleProps}>
        <TabGroup setTabMethod="merge" onChange={handleRunsTabClick}>
          <ResourceTabsSlot.Fill>
            <TabListV2>
              {getProjectTabs.map((tab) => (
                <TabLabelV2
                  key={tab.key}
                  label={tab.name}
                  onClick={
                    tab.key === 'insights'
                      ? () => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.delete('clusterJobId');
                          newParams.delete('clusterId');
                          newParams.delete('selectedLevel');
                          setSearchParams(newParams);
                        }
                      : undefined
                  }
                  badgeProps={tab.badgeProps}
                />
              ))}
            </TabListV2>
          </ResourceTabsSlot.Fill>
          <TabPanelsV2 className="flex flex-grow flex-col overflow-hidden overflow-y-auto">
            <TabPanelV2 className="h-full">
              <StatsSidebarLayout automate={false}>
                <ProjectRunsTableV2
                  sessionId={sessionId}
                  isTestRun={isTestRun}
                  searchModel={searchModel}
                  setSearchModel={setSearchModel}
                  updateSearchParams={updateSearchParams}
                />
              </StatsSidebarLayout>
            </TabPanelV2>

            {!isTestRun && (
              <TabPanelV2 className="h-full">
                <StatsSidebarLayout automate={false}>
                  <ProjectConversations
                    sessionId={sessionId}
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                  />
                </StatsSidebarLayout>
              </TabPanelV2>
            )}
            {!isTestRun && !(session?.name === 'evaluators') && (
              <TabPanelV2 className="h-full p-4">
                {formulasEnabled ? (
                  <SessionEvaluatorsTableV2
                    createEvaluatorAction={
                      <SessionRulesDropdown
                        trigger={
                          <Button size="md" leftDecorator={PlusIcon}>
                            New Evaluator
                          </Button>
                        }
                        togglePane={togglePane}
                        onEvaluatorUpdate={(evaluator) => {
                          setPrebuiltEvaluator(evaluator);
                        }}
                        popoverProps={{
                          side: 'left',
                          sideOffset: 4,
                          align: 'center',
                        }}
                        onStartAlignEvaluator={handStartAlignEvaluator}
                      />
                    }
                  />
                ) : (
                  <SessionEvaluatorsTable
                    createEvaluatorAction={
                      <SessionRulesDropdown
                        trigger={
                          <Button size="md" leftDecorator={PlusIcon}>
                            New Evaluator
                          </Button>
                        }
                        togglePane={togglePane}
                        onEvaluatorUpdate={(evaluator) => {
                          setPrebuiltEvaluator(evaluator);
                        }}
                        popoverProps={{
                          side: 'left',
                          sideOffset: 4,
                          align: 'center',
                        }}
                        onStartAlignEvaluator={handStartAlignEvaluator}
                      />
                    }
                  />
                )}
              </TabPanelV2>
            )}
            {!isTestRun && (
              <TabPanelV2 className="h-full p-4">
                <SessionRulesTable
                  onCreateRule={() => {
                    togglePane('automation', true);
                    setSelectedRule(null);
                  }}
                  onRuleSelect={(rule) => {
                    togglePane('automation', true);
                    setSelectedRule(rule);
                  }}
                />
              </TabPanelV2>
            )}
            <TabPanelV2 className="h-full">
              <InsightsJobsTable
                sessionId={sessionId}
                tabIndex={tabIndex}
                selectedClusterId={selectedClusterId}
                selectedClusterJobId={selectedClusterJobId}
                onRowClick={(cluster, event) => {
                  const clusterJobId = cluster.id;
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('clusterJobId', clusterJobId);

                  // If cmd + click or ctrl + click, open in new tab
                  if (event?.metaKey || event?.ctrlKey) {
                    window.open(
                      `${window.location.pathname}?${newParams.toString()}`,
                      '_blank'
                    );
                  } else {
                    setSearchParams(newParams);
                  }
                }}
              />
            </TabPanelV2>
          </TabPanelsV2>
        </TabGroup>
        {!isPublic && session && (
          <ResourceActionsSlot.Fill>
            <div className="flex items-center gap-2">
              {authorize('projects:update') && (
                <SessionCrudPaneWithButtonV2
                  session={session}
                  onSuccess={onSuccess}
                />
              )}

              {showTTLUI && (
                <ProjectTTLSetting project={session} variant="v2" />
              )}

              {authorize('charts:read') && (
                <ProjectMonitorSetting project={session} variant="v2" />
              )}

              {session?.extra?.deployment_id != null && (
                <Link
                  to={`/${appOrganizationPath}/${organizationId}/${deploymentsAppIndexPath}/${session.extra?.deployment_id}`}
                  target="_blank"
                >
                  <Button
                    color="secondary"
                    size="md"
                    leftDecorator={GraphFillIcon}
                  >
                    Deployment
                  </Button>
                </Link>
              )}

              {authorize('rules:create') && (
                <Link
                  to={`/${appOrganizationPath}/${organizationId}/${appDashboardsIndexPath}?tab=2&sessionId=${sessionId}`}
                  target="_blank"
                >
                  <Button
                    color="secondary"
                    size="md"
                    leftDecorator={BellRinging01Icon}
                  >
                    Alerts
                  </Button>
                </Link>
              )}
              {authorize('rules:create') && (
                <Popover
                  open={newPopoverOpen}
                  onOpenChange={(open) => {
                    setNewPopoverOpen(open);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      leftDecorator={PlusIcon}
                      color="primary"
                      size="md"
                      onClick={() => setNewPopoverOpen(true)}
                    >
                      New
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-30 flex flex-col gap-1 p-1"
                    align="end"
                  >
                    <ProjectNewActionPopoverTrigger
                      title="New Automation"
                      description="Set up workflows that run automatically based on your rules."
                      docsLink="https://docs.langchain.com/langsmith/rules"
                      icon={ZapFastIcon}
                      onClick={() => {
                        setSelectedRule(null);
                        togglePane('automation', true);
                        setNewPopoverOpen(false);
                      }}
                    />
                    {!(session?.name === 'evaluators') && (
                      <SessionRulesDropdown
                        trigger={
                          <ProjectNewActionPopoverTrigger
                            title="New Evaluator"
                            description="Measure performance with custom evaluation criteria."
                            docsLink="https://docs.langchain.com/langsmith/online-evaluations"
                            icon={Scales01Icon}
                          />
                        }
                        togglePane={togglePane}
                        onEvaluatorUpdate={(evaluator) => {
                          setPrebuiltEvaluator(evaluator);
                        }}
                        popoverProps={{
                          side: 'left',
                          sideOffset: 8,
                          align: 'start',
                        }}
                        onStartAlignEvaluator={handStartAlignEvaluator}
                      />
                    )}
                    <ProjectNewActionPopoverTrigger
                      title="New Alert"
                      description="Get notified when important events or conditions occur."
                      docsLink="https://docs.langchain.com/langsmith/alerts"
                      icon={BellPlusIcon}
                      onClick={() => {
                        togglePane('alerts', true);
                        setNewPopoverOpen(false);
                      }}
                    />
                    {clioEnabled && (
                      <ProjectNewActionPopoverTrigger
                        title="New Insights"
                        badge="Beta"
                        description="Group and summarize your data into clear themes."
                        icon={Lightbulb02Icon}
                        onClick={() => {
                          togglePane('clusterJobs', true);
                          setNewPopoverOpen(false);
                        }}
                      />
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </ResourceActionsSlot.Fill>
        )}
        <RuleEditPane
          open={paneState.activePane === 'automation'}
          onClose={() => togglePane('automation', false)}
          sessionId={sessionId}
          id={selectedRule?.id}
          initialData={selectedRule ?? undefined}
          onSuccess={() => {
            createToast({
              title: selectedRule
                ? 'Rule updated successfully'
                : 'Rule created successfully',
            });
            mutateRunRules();
            togglePane('automation', false);
            setSelectedRule(null);
            navigate(
              `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}?tab=3`
            );
          }}
          runTableFilters={searchModel}
          setRunTableFilters={setSearchModel}
        />
        <AlertsCreatePane
          isOpen={paneState.activePane === 'alerts'}
          onClose={() => togglePane('alerts', false)}
          sessionId={sessionId}
          projectName={session?.name}
          onSuccess={() => {
            createToast({
              title: 'Alert created successfully',
            });
          }}
        />
        {clioEnabled && (
          <InsightsJobConfig
            isOpen={paneState.activePane === 'clusterJobs'}
            onClose={() => togglePane('clusterJobs', false)}
            sessionId={sessionId}
            onSuccess={() => togglePane('clusterJobs', false)}
          />
        )}

        <SessionsEvaluatorRuleCrudPane
          sessionId={sessionId}
          open={paneState.activePane === 'llm'}
          setOpen={(open) => {
            togglePane('llm', open);
          }}
          evaluator={prebuiltEvaluator}
          onSave={handleEvaluatorSave}
          runTableFilters={searchModel}
          setRunTableFilters={setSearchModel}
        />

        <ThreadEvaluatorCrudPane
          sessionId={sessionId}
          open={paneState.activePane === 'thread'}
          setOpen={(open) => {
            togglePane('thread', open);
          }}
          onSave={handleEvaluatorSave}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
        <SessionsCustomCodeEvaluatorCrudPane
          open={paneState.activePane === 'code'}
          setOpen={(open) => togglePane('code', open)}
          sessionId={sessionId}
          runTableFilters={searchModel}
          setRunTableFilters={setSearchModel}
          onSaveSuccess={handleEvaluatorSave}
        />
        {formulasEnabled && (
          <CompositeScorePane
            open={paneState.activePane === 'compositeScore'}
            onClose={() => togglePane('compositeScore', false)}
            sessionId={sessionId}
            onSaved={handleEvaluatorSave}
          />
        )}
      </ResourceDetailLayout>
    </ProjectSidebarSlot.Context>
  );
};

const Project = () => {
  const { sessionId } = useParams();

  // Fast loading session data for page title (without stats)
  const sessionBasicSwr = useSession(
    sessionId ?? null,
    undefined, // No include_stats for faster loading
    {
      revalidateOnFocus: false,
    }
  );

  // Full session data with stats for components that need it
  const sessionWithStatsSwr = useSession(
    sessionId ?? null,
    { include_stats: true },
    {
      revalidateOnFocus: false,
    }
  );

  const onSuccess = (_id: string, session: SessionSchema) => {
    sessionBasicSwr?.mutate(session);
    sessionWithStatsSwr?.mutate(session);
  };

  return sessionId ? (
    <ProjectPageV2
      sessionId={sessionId}
      session={sessionWithStatsSwr?.data || sessionBasicSwr?.data} // Use stats data if available, otherwise basic data
      onSuccess={onSuccess}
      isLoading={sessionBasicSwr?.isLoading}
    />
  ) : null;
};

export default Project;
